import { getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { firestore } from '../firebaseConfig'; // Firestore setup
import moment from 'moment-timezone';
import { getWordsData } from './utils';
import { remainingReviewAllowance, reviewDueCutoff, UNLIMITED } from '../utils/srs';

// Function to fetch the actual sentence data
const getSentenceData = async (sentences) => {
  // Create an array of promises to fetch each sentence document and its words in parallel
  const sentencePromises = sentences.map(async (sentence) => {
    const sentenceRef = sentence.sentence_ref;
    const sentenceDoc = await getDoc(sentenceRef);

    if (sentenceDoc.exists()) {
      const sentenceData = { ...sentence, ...sentenceDoc.data() };
      // Fetch the associated words in parallel
      sentenceData.words = await getWordsData(sentenceData.words || []);
      return sentenceData; // Return the sentence data
    } else {
      return null; // If the document doesn't exist, return null
    }
  });

  // Resolve all the promises in parallel
  const sentencesData = await Promise.all(sentencePromises);

  // Filter out any null values (non-existent documents)
  return sentencesData.filter((sentence) => sentence !== null);
};

// Main function to fetch review sentences
export const fetchReviewSentencesSessionItems = async (userId, userData, reviewedSentencesData, isSubscribed) => {
  try {
    if (!userId || !userData) {
      throw new Error('Missing required parameters: userId and userData');
    }

    const userTimezone = userData.settings?.timezone || 'UTC';
    const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');

    const toReviewCount = remainingReviewAllowance(userData.settings?.sentence_reviews_per_day, reviewedSentencesData?.[todayDate]);

    if (userData.settings?.vacation_started_at) {
      return []; // vacation
    }

    // Allowance already spent. Returning early also keeps limit(0) out of the
    // query below — the SDK rejects a non-positive limit.
    if (toReviewCount === 0) {
      return [];
    }

    const currTime = reviewDueCutoff(userData.settings?.review_frequency, userTimezone);

    const userSentencesCollectionRef = collection(firestore, 'users', userId, 'user_sentences');
    const sentencesToReview = [];

    // Query to fetch sentences that need to be reviewed before today, limited by the number to review
    let querySnapshot;
    const baseQuery = query(
      userSentencesCollectionRef,
      where('next_review', '<=', currTime),
      where('srs_stage', '<', userData.settings?.review_burned_items ? 8 : 7),
      where('skipped', '==', false),
      where('level', '>=', isSubscribed ? 0 : 5),
      orderBy('next_review')
    );
    if (toReviewCount === UNLIMITED) {
      querySnapshot = await getDocs(baseQuery);
    } else {
      querySnapshot = await getDocs(query(baseQuery, limit(toReviewCount)));
    }
    querySnapshot.forEach((doc) => {
      sentencesToReview.push({ id: doc.id, ...doc.data() });
    });

    const sentencesData = await getSentenceData(sentencesToReview);

    return sentencesData;
  } catch (error) {
    console.error('Error fetching review sentences:', error);
    throw error;
  }
};
